import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, RotateCcw, Settings, Type } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const PrintSettings = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [settings, setSettings] = useState({
    arial_line_spacing_multiplier: 2.5,
    updated_at: null,
    updated_by: null
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/print-settings');
      if (response.data.settings) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching print settings:', error);
      toast.error('Failed to load print settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLineSpacingChange = (value) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 1 && numValue <= 5) {
      setSettings(prev => ({
        ...prev,
        arial_line_spacing_multiplier: numValue
      }));
    }
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      await apiClient.put('/print-settings', {
        arial_line_spacing_multiplier: settings.arial_line_spacing_multiplier
      });
      toast.success('Print settings saved successfully');
      fetchSettings();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to save settings';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset print settings to defaults?')) return;
    
    setIsSubmitting(true);
    try {
      await apiClient.post('/print-settings/reset');
      toast.success('Print settings reset to defaults');
      fetchSettings();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to reset settings';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/dashboard')}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Print Settings</h1>
              <p className="text-sm text-[#E8DCC8]">Configure global printing options</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleReset}
              variant="outline"
              disabled={isSubmitting || isLoading}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="reset-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset to Defaults
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSubmitting || isLoading}
              className="bg-[#2D5F4F] hover:bg-[#234A3D]"
              data-testid="save-btn"
            >
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Settings
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {isLoading ? (
          <div className="text-center py-12" data-testid="loading-spinner">
            <div className="inline-block w-8 h-8 border-4 border-[#2D5F4F] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* Font Info */}
            <Card className="border-2 border-[#E5DFD0]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Type className="w-5 h-5 text-amber-600" />
                  Label Font
                </CardTitle>
                <CardDescription>
                  All Main Labels use Arial Bold (ARIALB.TTF) font.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>Font sizes</strong> are now configured per-field in each template.
                    Go to <button onClick={() => navigate('/admin/main-label-templates')} className="underline text-amber-700 hover:text-amber-900">Main Label Templates</button> to configure font sizes for each field (Quality, Content, Size, MRP, etc.).
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Line Spacing Settings */}
            <Card className="border-2 border-[#E5DFD0]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Settings className="w-5 h-5 text-blue-600" />
                  Consumer Notice Line Spacing
                </CardTitle>
                <CardDescription>
                  Configure line spacing for the Consumer Notice text which spans multiple lines.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-w-sm">
                  <Label className="font-medium">Line Spacing Multiplier</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="1"
                    max="5"
                    value={settings.arial_line_spacing_multiplier}
                    onChange={(e) => handleLineSpacingChange(e.target.value)}
                    className="mt-2"
                    data-testid="line-spacing-input"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Higher values increase spacing between lines. Default: 2.5
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Formula: Line Height = Font Size × Multiplier
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Last Updated Info */}
            {settings.updated_at && (
              <div className="text-sm text-gray-500 text-center">
                Last updated: {new Date(settings.updated_at).toLocaleString()} by {settings.updated_by}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default PrintSettings;
